/**
 * src/shell/portal/components/media/UniversalMediaComposerModal.tsx
 *
 * Intelligent Media Composer Modal:
 * Auto-unfurls YouTube, Vimeo, Whiteboard URIs, and PDFs with real-time preview,
 * session state toggles (Live Now vs Pre-recorded), and custom metadata overrides.
 */

import React, { useState, useEffect } from 'react';
import { Monitor } from 'lucide-react';
import type { MediaFeedItem, SessionState } from '../../../../domain/media/media-feed-contracts.js';
import { MediaClassifier } from '../../../../domain/media/media-classifier.js';
import { OEmbedService } from '../../../../domain/media/oembed-service.js';
import { ScreenShareLegalModal } from './ScreenShareLegalModal.js';

export interface UniversalMediaComposerModalProps {
  channelId?: string;
  channelName?: string;
  onClose: () => void;
  onPublish: (item: MediaFeedItem) => void;
}

export const UniversalMediaComposerModal: React.FC<UniversalMediaComposerModalProps> = ({
  channelId,
  channelName,
  onClose,
  onPublish,
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [sessionState, setSessionState] = useState<SessionState>('live');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);

  // Auto-detect classification
  const classification = MediaClassifier.classify(urlInput);

  const handleStartScreenShare = (warrantySignature: string) => {
    setIsLegalModalOpen(false);
    const roomId = `room_scr_${Date.now()}`;
    const screencastItem: MediaFeedItem = {
      id: `media_${roomId}`,
      creatorDid: 'did:herx:p_admin',
      creatorHandle: '@admin.herx',
      createdAt: Date.now(),
      type: 'screen_share',
      sessionState: 'live',
      title: title.trim() || 'Live Ephemeral Watch Party',
      description: description.trim() || 'Interactive peer-to-peer screen share session over sovereign mesh.',
      thumbnailUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1200&q=80',
      tags: ['watch_party', 'screenshare'],
      payload: {
        screenShare: {
          roomId,
          roomUri: `hts://mesh/screencast/${roomId}?ttl=14400`,
          hostDid: 'did:herx:p_admin',
          hostHandle: '@admin.herx',
          maxViewers: 3,
          activeViewerCount: 1,
          expiresAt: Date.now() + 4 * 3600 * 1000,
          hasAudio: true,
          resolution: '720p30',
          targetBitrateKbps: 1800,
          isEncryptedP2P: true,
          tier: 'free',
          warrantySignature,
        },
      },
      stats: { views: 1, likes: 0, commentCount: 0 },
      context: { channelId, channelName },
    };
    onPublish(screencastItem);
    onClose();
  };

  useEffect(() => {
    let isCancelled = false;
    if (classification.type === 'video_embed' && urlInput.trim()) {
      setIsLoadingMetadata(true);
      OEmbedService.fetchMetadata(urlInput.trim())
        .then((meta) => {
          if (!isCancelled) {
            if (!title) setTitle(meta.title);
            setThumbnailUrl(meta.thumbnailUrl);
            if (meta.isLive) setSessionState('live');
          }
        })
        .finally(() => {
          if (!isCancelled) setIsLoadingMetadata(false);
        });
    }
    return () => {
      isCancelled = true;
    };
  }, [urlInput]);

  const handlePublish = (e: React.FormEvent) => {
    e.preventDefault();
    const finalTitle = title.trim() || classification.suggestedTitle || 'New Media Session';

    const item: MediaFeedItem = {
      id: `media_${Date.now()}`,
      creatorDid: 'did:mesh:me',
      creatorHandle: '@current.user',
      createdAt: Date.now(),
      type: classification.type,
      sessionState,
      title: finalTitle,
      description: description.trim() || undefined,
      thumbnailUrl: thumbnailUrl || undefined,
      tags: tagsInput.split(',').map((t) => t.trim().replace(/^#/, '')).filter(Boolean),
      payload: {
        video: classification.type === 'video_embed' && classification.meta.platformId ? {
          provider: classification.meta.provider || 'youtube',
          platformId: classification.meta.platformId,
          embedUrl: classification.meta.embedUrl || '',
          isLiveStream: sessionState === 'live',
        } : undefined,
        whiteboard: classification.type === 'whiteboard' ? {
          roomUri: classification.meta.roomUri || 'hts://mesh/whiteboard/session-default',
          canCollaborate: true,
          strokeCount: 0,
        } : undefined,
        document: classification.type === 'document_pdf' ? {
          fileUrl: classification.meta.fileUrl || urlInput,
          pageCount: 1,
          downloadable: true,
        } : undefined,
      },
      stats: {
        views: 1,
        likes: 0,
        commentCount: 0,
      },
      comments: [],
      context: channelId ? {
        channelId,
        channelName: channelName || '#general',
      } : undefined,
    };

    onPublish(item);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '560px',
          backgroundColor: 'var(--theme-bg-surface, #0c1017)',
          borderRadius: '16px',
          border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--theme-bg-card, #090d16)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>📡</span>
            <h3 style={{ margin: 0, color: 'var(--theme-text-primary, #f8fafc)', fontSize: '16px', fontWeight: 700 }}>
              Broadcast or Embed Sovereign Media
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--theme-text-muted, #94a3b8)',
              fontSize: '18px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handlePublish} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Quick Action: Start Screen Share / Watch Party */}
          <div
            style={{
              padding: '12px 14px',
              borderRadius: '10px',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Monitor size={18} color="var(--theme-accent, #6366f1)" />
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--theme-text-primary, #f8fafc)' }}>
                  Live Screen Share & Watch Party
                </div>
                <div style={{ fontSize: '10px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                  Stream desktop or browser tab directly to peers with system audio
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsLegalModalOpen(true)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                backgroundColor: 'var(--theme-accent, #6366f1)',
                color: 'var(--theme-text-primary, #ffffff)',
                border: 'none',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Start Stream
            </button>
          </div>

          {/* URL Input with Auto-detection Badge */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)' }}>
                Media URL or Whiteboard URI
              </label>
              {urlInput.trim() && (
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(56, 189, 248, 0.15)',
                    color: 'var(--theme-accent, #38bdf8)',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    textTransform: 'uppercase',
                  }}
                >
                  Detected: {classification.type.replace('_', ' ')}
                </span>
              )}
            </div>
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="Paste YouTube Live, Vimeo, Whiteboard URI, or PDF link..."
              autoFocus
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-bg-input, #121824)',
                border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.12))',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {isLoadingMetadata && (
              <span style={{ fontSize: '11px', color: 'var(--theme-accent, #38bdf8)', marginTop: '4px', display: 'inline-block' }}>
                Fetching oEmbed metadata & high-res poster...
              </span>
            )}
          </div>

          {/* Session State Selection */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', display: 'block', marginBottom: '8px' }}>
              Broadcast Session Mode
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '8px',
                  backgroundColor: sessionState === 'live' ? 'rgba(239, 68, 68, 0.15)' : 'var(--theme-bg-input, #121824)',
                  border: sessionState === 'live' ? '1px solid var(--theme-status-danger, #ef4444)' : '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <input
                  type="radio"
                  name="sessionState"
                  checked={sessionState === 'live'}
                  onChange={() => setSessionState('live')}
                  style={{ accentColor: 'var(--theme-status-danger, #ef4444)' }}
                />
                <div>
                  <div style={{ color: sessionState === 'live' ? 'var(--theme-status-danger, #ef4444)' : 'var(--theme-text-primary, #f8fafc)', fontWeight: 700, fontSize: '12px' }}>
                    🔴 Live Stream / Session
                  </div>
                  <div style={{ color: 'var(--theme-text-muted, #64748b)', fontSize: '10px' }}>Active live channel broadcast</div>
                </div>
              </label>

              <label
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '8px',
                  backgroundColor: sessionState === 'prerecorded' ? 'rgba(56, 189, 248, 0.15)' : 'var(--theme-bg-input, #121824)',
                  border: sessionState === 'prerecorded' ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <input
                  type="radio"
                  name="sessionState"
                  checked={sessionState === 'prerecorded'}
                  onChange={() => setSessionState('prerecorded')}
                  style={{ accentColor: 'var(--theme-accent, #38bdf8)' }}
                />
                <div>
                  <div style={{ color: sessionState === 'prerecorded' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-primary, #f8fafc)', fontWeight: 700, fontSize: '12px' }}>
                    📼 Pre-recorded / Replay
                  </div>
                  <div style={{ color: 'var(--theme-text-muted, #64748b)', fontSize: '10px' }}>Archived session recording</div>
                </div>
              </label>
            </div>
          </div>

          {/* Title Override */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', display: 'block', marginBottom: '6px' }}>
              Session Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Decentralized Mesh Relay Workshop #42"
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-bg-input, #121824)',
                border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.12))',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '13px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Description & Tags */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', display: 'block', marginBottom: '6px' }}>
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief summary..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--theme-bg-input, #121824)',
                  border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.12))',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  fontSize: '12px',
                  outline: 'none',
                  resize: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', display: 'block', marginBottom: '6px' }}>
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="mesh, p2p, esp32"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--theme-bg-input, #121824)',
                  border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.12))',
                  color: 'var(--theme-text-primary, #f8fafc)',
                  fontSize: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                color: 'var(--theme-text-muted, #94a3b8)',
                border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!urlInput.trim() && !title.trim()}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: 'var(--theme-text-inverse, #0f172a)',
                border: 'none',
                fontSize: '13px',
                fontWeight: 700,
                cursor: urlInput.trim() || title.trim() ? 'pointer' : 'not-allowed',
                boxShadow: '0 4px 14px rgba(56, 189, 248, 0.4)',
              }}
            >
              Publish to Feed
            </button>
          </div>
        </form>
      </div>

      {/* Screen Share Compliance & Factual Warranty Modal (Sprint 14) */}
      <ScreenShareLegalModal
        isOpen={isLegalModalOpen}
        hostDid="did:herx:p_admin"
        onClose={() => setIsLegalModalOpen(false)}
        onAccept={handleStartScreenShare}
      />
    </div>
  );
};
